import {
  Chip,
  Stack,
  Typography,
} from "@mui/material";

import {
  GridActionsCellItem,
  GridColDef,
} from "@mui/x-data-grid";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import HistoryIcon from "@mui/icons-material/History";
import VisibilityIcon from "@mui/icons-material/Visibility";

import AppDataTable from "../../../../components/common/AppDataTable";
import AppStatusSwitch from "../../../../components/common/AppStatusSwitch";

import { Inventory } from "../types";

interface InventoryTableProps {
  rows: Inventory[];

  loading: boolean;

  page: number;

  pageSize: number;

  rowCount: number;

  onPageChange: (page: number) => void;

  onPageSizeChange: (pageSize: number) => void;

  onEdit: (inventory: Inventory) => void;

  onView: (inventory: Inventory) => void;

  onDelete: (inventory: Inventory) => void;

  onHistory: (inventory: Inventory) => void;

  onStatusChange: (
    inventory: Inventory,
    checked: boolean
  ) => void;
}

const InventoryTable = ({
  rows,
  loading,
  page,
  pageSize,
  rowCount,
  onPageChange,
  onPageSizeChange,
  onEdit,
  onView,
  onDelete,
  onHistory,
  onStatusChange,
}: InventoryTableProps) => {
  const columns: GridColDef[] = [
    {
      field: "sparePartName",
      headerName: "Spare Part",
      flex: 1.5,
      minWidth: 220,
    },

    {
      field: "partCode",
      headerName: "Part Code",
      flex: 1,
      minWidth: 130,
    },

    {
      field: "categoryName",
      headerName: "Category",
      flex: 1,
      minWidth: 170,
    },

    {
      field: "warehouseName",
      headerName: "Warehouse",
      flex: 1.2,
      minWidth: 180,
    },

    {
      field: "availableStock",
      headerName: "Available",
      width: 120,
      align: "center",
      headerAlign: "center",
    },

    {
      field: "reservedStock",
      headerName: "Reserved",
      width: 120,
      align: "center",
      headerAlign: "center",
    },

    {
      field: "totalStock",
      headerName: "Total",
      width: 120,
      align: "center",
      headerAlign: "center",
    },

    {
      field: "reorderLevel",
      headerName: "Reorder",
      width: 120,
      align: "center",
      headerAlign: "center",
    },

    {
      field: "weightedAverageUnitCost",
      headerName: "Avg Cost",
      width: 130,
      align: "right",
      headerAlign: "right",
      valueFormatter: ({ value }) => `Rs ${Number(value || 0).toLocaleString()}`,
    },

    {
      field: "inventoryValue",
      headerName: "Value",
      width: 140,
      align: "right",
      headerAlign: "right",
      valueFormatter: ({ value }) => `Rs ${Number(value || 0).toLocaleString()}`,
    },

    {
      field: "stockStatus",
      headerName: "Stock Status",
      width: 150,
      sortable: false,

      renderCell: (params) => {
        const available =
          params.row.availableStock;

        const reorder =
          params.row.reorderLevel;

        if (available === 0) {
          return (
            <Chip
              label="Out of Stock"
              color="error"
              size="small"
            />
          );
        }

        if (available <= reorder) {
          return (
            <Chip
              label="Low Stock"
              color="warning"
              size="small"
            />
          );
        }

        return (
          <Chip
            label="In Stock"
            color="success"
            size="small"
          />
        );
      },
    },

    {
      field: "lastUpdated",
      headerName: "Last Updated",
      width: 180,

      renderCell: (params) => (
        <Typography variant="body2">
          {params.value || "-"}
        </Typography>
      ),
    },

    {
      field: "status",
      headerName: "Status",
      width: 150,

      sortable: false,

      renderCell: (params) => (
        <AppStatusSwitch
          checked={
            params.row.status ===
            "ACTIVE"
          }
          onChange={(checked) =>
            onStatusChange(
              params.row,
              checked
            )
          }
        />
      ),
    },

    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 220,

      getActions: (params) => [
        <GridActionsCellItem
          icon={<VisibilityIcon />}
          label="View Details"
          onClick={(event) => {
            event.stopPropagation();
            onView(params.row)
          }}
        />,

        <GridActionsCellItem
          icon={<EditIcon />}
          label="Edit"
          onClick={(event) => {
            event.stopPropagation();
            onEdit(params.row)
          }}
        />,

        <GridActionsCellItem
          icon={<HistoryIcon />}
          label="History"
          onClick={(event) => {
            event.stopPropagation();
            onHistory(params.row)
          }}
        />,

        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Delete"
          onClick={(event) => {
            event.stopPropagation();
            onDelete(params.row)
          }}
        />,
      ],
    },
  ];

  return (
    <AppDataTable
      rows={rows}
      columns={columns}
      loading={loading}
      page={page}
      pageSize={pageSize}
      rowCount={rowCount}
      onRowClick={(params) => onView(params.row)}
      onPageChange={onPageChange}
      onPageSizeChange={
        onPageSizeChange
      }
      sx={{
        "& .MuiDataGrid-row": {
          cursor: "pointer",
        },
      }}
    />
  );
};

export default InventoryTable;
